/* eslint-disable react/jsx-props-no-spreading */
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { hoist, QueryProvider } from '@thd-nucleus/data-sources';
import { Hydrator } from '@thd-olt-component-react/hydrator';
import { Col } from '@thd-olt-component-react/core-ui';
import { ProductPodPlaceholder } from '@thd-olt-component-react/product-pod';
import { ResultsWrapped } from '@thd-olt-component-react/product-results';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';

import { CustomProductPodResponsive } from '../browse-search/CustomProductPodResponsive';

const HoistedResultsWrapped = hoist(ResultsWrapped, CustomProductPodResponsive);

export const PepProductList = (props) => {
  const {
    products,
    isClientResolved,
    cacheId,
    mountedFn,
    skipFn,
    newDefaultVariables,
    colFallback,
    colXs,
    storeId,
    hasInStoreFilter,
    handleProductClick,
    samplesLoading,
    hydratorId,
    erroClassName,
    isMobileOrConsumerApp,
    maxNumOfPodsToHydrate = 12
  } = props;
  const [isHydrated, setIsHydrated] = useState(false);
  const CLIENT_SIDE_PRODUCT_POD_COUNT = 24;
  const SERVER_SIDE_PRODUCT_POD_COUNT = 12;
  const HYDRATION_STANDARD_SCROLL_BUFFER = 750;

  const productsReduce = (productPods) => {
    let groupOfPods = 0;
    const isClientSearchModelResolved = isClientResolved({ queryName: 'searchModel' });
    let reduced = productPods?.reduce((hydrator, product) => {
      if (!hydrator[groupOfPods]) {
        hydrator.push([]);
      }
      if (hydrator[groupOfPods].length < maxNumOfPodsToHydrate) {
        hydrator[groupOfPods].push(product);
        if (hydrator[groupOfPods].length === maxNumOfPodsToHydrate) {
          groupOfPods += 1;
        }
      }
      return hydrator;
    }, []);

    if (
      productPods.length === SERVER_SIDE_PRODUCT_POD_COUNT
      && !isClientSearchModelResolved
    ) {
      const placeholderHydrators = (CLIENT_SIDE_PRODUCT_POD_COUNT - SERVER_SIDE_PRODUCT_POD_COUNT)
        / maxNumOfPodsToHydrate;
      for (let i = 0; i < placeholderHydrators; i += 1) {
        reduced.push(Array(maxNumOfPodsToHydrate).fill());
      }
    }

    return reduced;
  };

  const originalProducts = useRef(productsReduce(products));
  const hydrators = isHydrated || !originalProducts.current?.length
    ? productsReduce(products)
    : originalProducts.current;
  const fallbackHydrators = Array(2).fill();

  return (
    <>
      {!hydrators?.length
        ? fallbackHydrators.map((group) => {
          return <ProductPodPlaceholder podsCount={SERVER_SIDE_PRODUCT_POD_COUNT} />;
        }) : (
          <QueryProvider
            cacheKey={cacheId}
            dataSource="searchNav"
            defaultVariables={newDefaultVariables}
            mounted={mountedFn}
            skip={skipFn}
          >{hydrators?.map((group, hydratorIndex) => {
            if (!group?.length) {
              return <ProductPodPlaceholder podsCount={SERVER_SIDE_PRODUCT_POD_COUNT} />;
            }
            return (
              <Hydrator
                immediateRender={!hydratorIndex}
                afterHydrate={() => {
                  setIsHydrated(true);
                }}
                className="grid"
                id={`${hydratorId}${hydratorIndex + 1}`}
                key={`hydrator-${hydratorIndex}`}
                patch={!hydratorIndex}
                scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                placeholder={
                  <ProductPodPlaceholder podsCount={SERVER_SIDE_PRODUCT_POD_COUNT} />
                }
              >
                <HoistedResultsWrapped>
                  {group.map((product, itemIndex) => {
                    const { itemId: skuId, canonicalUrl } = product?.identifiers || {};
                    if (!skuId) return null;
                    const podPosition = isMobileOrConsumerApp
                      ? hydratorIndex * maxNumOfPodsToHydrate + itemIndex : itemIndex;
                    return (
                      <ErrorBoundary
                        id={`plp-product-pod__${itemIndex}`}
                        index={itemIndex}
                        key={`${skuId}-${itemIndex}`}
                        name={`Product Pod: ${itemIndex}`}
                        className={erroClassName}
                      >
                        <Col
                          className="pep__pod"
                          fallback={colFallback}
                          lg="3"
                          md="3"
                          sm="3"
                          xs={colXs}
                        >
                          <CustomProductPodResponsive
                            parent="pep"
                            itemId={skuId}
                            url={canonicalUrl}
                            storeId={storeId}
                            onHover={() => { }}
                            hoverDelay={0}
                            position={podPosition}
                            itemRecsIndex={itemIndex}
                            hasInStoreFilter={hasInStoreFilter}
                            onClick={handleProductClick}
                            samplesLoading={samplesLoading}
                            isMobileOrConsumerApp={isMobileOrConsumerApp}
                          />
                        </Col>
                      </ErrorBoundary>
                    );
                  })}
                </HoistedResultsWrapped>
              </Hydrator>
            );
          })}
          </QueryProvider>
        )}
    </>
  );
};

PepProductList.displayName = 'PepProductList';

PepProductList.propTypes = {
  products: PropTypes.arrayOf({}),
  isClientResolved: PropTypes.func,
  cacheId: PropTypes.string,
  mountedFn: PropTypes.func,
  skipFn: PropTypes.func,
  newDefaultVariables: PropTypes.shape({}),
  colFallback: PropTypes.string,
  colXs: PropTypes.string,
  storeId: PropTypes.string,
  hasInStoreFilter: PropTypes.func,
  handleProductClick: PropTypes.func,
  samplesLoading: PropTypes.bool,
  hydratorId: PropTypes.string,
  erroClassName: PropTypes.string,
  isMobileOrConsumerApp: PropTypes.bool,
  maxNumOfPodsToHydrate: PropTypes.number
};

PepProductList.defaultProps = {
  products: [],
  isClientResolved: PropTypes.func,
  cacheId: '',
  mountedFn: () => {},
  skipFn: () => {},
  newDefaultVariables: {},
  colFallback: () => {},
  colXs: '',
  storeId: '',
  hasInStoreFilter: () => {},
  handleProductClick: () => {},
  samplesLoading: false,
  hydratorId: '',
  erroClassName: '',
  isMobileOrConsumerApp: false,
  maxNumOfPodsToHydrate: 12
};
